// Blue Harbor Labs — Organized Flow logomark.
//
// Replaces the prior anchor/lighthouse mark. The mark visualises the BHL
// product narrative — Connect → Organize → Understand → Act — as four
// nodes joined by a single continuous flow path. The terminal node is
// gold to read as the outcome ("Act").
//
// Two tones:
//   tone="light"  → for dark backgrounds (e.g. the navy sidebar)
//   tone="dark"   → for light backgrounds (e.g. cards, landing pages)
//
// Sizes default to 24px and scale with the surrounding className.

const COLORS = {
  navy: "#0d3b66",
  gold: "#f4d35e",
  white: "#ffffff",
  lightLine: "rgba(255,255,255,0.55)",
  darkLine: "rgba(13,59,102,0.45)",
};

export const BHLMark = ({
  tone = "dark",
  size = 24,
  className,
  title = "Blue Harbor Labs — Organized Flow",
}) => {
  const isLight = tone === "light";
  const stroke = isLight ? COLORS.white : COLORS.navy;
  const lineSoft = isLight ? COLORS.lightLine : COLORS.darkLine;
  const nodeFill = isLight ? COLORS.navy : COLORS.white;
  // Four nodes: top-left, top-right, bottom-right, bottom-left.
  // The path threads through them in order; the final node is gold.
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      width={size}
      height={size}
      role="img"
      aria-label={title}
      className={className}
      data-testid="bhl-mark"
    >
      <title>{title}</title>
      {/* Soft connection grid (sits behind the main flow path) */}
      <g stroke={lineSoft} strokeWidth="1.5" strokeLinecap="round" fill="none">
        <path d="M9 9 L23 9" />
        <path d="M23 9 L23 23" />
        <path d="M23 23 L9 23" />
        <path d="M9 23 L9 9" />
      </g>
      {/* Continuous flow path: top-left → top-right → bottom-right → bottom-left.
          Slight curvature suggests motion / connected progression. */}
      <path
        d="M9 9 C 14 6, 19 6, 23 9
           S 27 16, 23 23
           S 14 27, 9 23"
        fill="none"
        stroke={stroke}
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Stage nodes — Connect, Organize, Understand, Act */}
      {/* Connect */}
      <circle cx="9" cy="9" r="2.5" fill={stroke} />
      <circle cx="9" cy="9" r="1.1" fill={nodeFill} />
      {/* Organize */}
      <circle cx="23" cy="9" r="2.5" fill={stroke} />
      <circle cx="23" cy="9" r="1.1" fill={nodeFill} />
      {/* Understand */}
      <circle cx="23" cy="23" r="2.5" fill={stroke} />
      <circle cx="23" cy="23" r="1.1" fill={nodeFill} />
      {/* Act — terminal gold node */}
      <circle cx="9" cy="23" r="3" fill={COLORS.gold} stroke={stroke} strokeWidth="1.5" />
    </svg>
  );
};

/**
 * Full brand lockup: mark + "Blue Harbor Labs" wordmark.
 * Used on the landing page and any large surface that needs the full name.
 */
export const BHLWordmark = ({
  tone = "dark",
  align = "left",
  size = "md",
  testId,
}) => {
  const isLight = tone === "light";
  const fontMain = isLight ? "text-white" : "text-[#0d3b66]";
  const fontSub = isLight ? "text-white/60" : "text-slate-500";
  const sizes = {
    sm: { mark: 22, label: "text-sm", caption: "text-[10px]" },
    md: { mark: 36, label: "text-lg", caption: "text-[11px]" },
    lg: { mark: 56, label: "text-2xl", caption: "text-xs" },
  };
  const s = sizes[size] || sizes.md;
  return (
    <div
      data-testid={testId || "bhl-wordmark"}
      className={`inline-flex items-center gap-3 ${
        align === "center" ? "justify-center" : ""
      }`}
    >
      <BHLMark tone={tone} size={s.mark} />
      <div className="leading-tight">
        <div
          className={`uppercase tracking-[0.18em] font-semibold ${fontSub} ${s.caption}`}
        >
          Blue Harbor Labs
        </div>
        <div className={`${fontMain} font-semibold tracking-tight ${s.label}`}>
          Organized Flow
        </div>
      </div>
    </div>
  );
};
